<template>
  <article class="war-room-post" data-test="war_room_post">
    <WarRoomSchema v-if="post.category.slug === 'question' && post.rating >= 0" :post="post" />
    <WarRoomPostOfficialHeader v-if="post.is_official" :post="post" />
    <WarRoomPostAuthor v-if="!post.is_official" :post="post" />
    <WarRoomDealStoryPostHeader
      v-if="isDealStory"
      :author="post.user_username"
      :company="dealCompany"
      :amount="post.content.data.deal.amount"
      :departments="dealDepartments"
    />
    <BravadoText v-else tag="h1" variant="header4" class="war-room-post__title" data-test="post-title">
      {{ post.content.data.title }}
    </BravadoText>
    <WarRoomPostContent :content="post.content" />
    <WarRoomPostPoll
      v-if="post.poll"
      :poll="post.poll"
      class="war-room-post__poll"
      :is-author="isAuthor"
      @vote="onPollVote"
    />
    <WarRoomPostFilesAttached v-if="post.files.length" :files="post.files" class="war-room-post__files" />
    <WarRoomPostTopics :topics="topics" class="war-room-post__topics" />
    <div class="war-room-post__actions">
      <WarRoomPostVote :post="post" class="war-room-post__vote" />
      <WarRoomShare :sharable-type="sharableType" :sharable-url="sharableUrl" :sharable-id="post.id" />
    </div>

    <ClientOnly>
      <WarRoomUserSelector />
      <WarRoomConfirmRemoveComment />
      <WarRoomConfirmHideComment />
    </ClientOnly>
  </article>
</template>

<script lang="ts" setup>
import Vue from 'vue';
import { computed, useContext, useRouter, useStore } from '@nuxtjs/composition-api';
import WarRoomConfirmHideComment from '@/components/partials/war-room/WarRoomConfirmHideComment.vue';
import WarRoomConfirmRemoveComment from '@/components/partials/war-room/WarRoomConfirmRemoveComment.vue';
import WarRoomDealStoryPostHeader from '@/components/partials/war-room/WarRoomDeal/WarRoomDealStoryPostHeader.vue';
import WarRoomPostAuthor from '@/components/partials/war-room/WarRoomPostAuthor.vue';
import WarRoomPostContent from '@/components/partials/war-room/WarRoomPostContent/WarRoomPostContent.vue';
import WarRoomPostFilesAttached from '@/components/partials/war-room/WarRoomPostFilesAttached.vue';
import WarRoomPostOfficialHeader from '@/components/partials/war-room/WarRoomPostOfficialHeader/WarRoomPostOfficialHeader.vue';
import WarRoomPostPoll from '@/components/partials/war-room/WarRoomPostPoll/WarRoomPostPoll.vue';
import WarRoomPostTopics from '@/components/partials/war-room/WarRoomPostTopics.vue';
import WarRoomPostVote from '@/components/partials/war-room/WarRoomVote/WarRoomPostVote.vue';
import WarRoomSchema from '@/components/partials/war-room/WarRoomSchema/WarRoomSchema.vue';
import WarRoomShare from '@/components/partials/war-room/WarRoomShare/WarRoomShare.vue';
import WarRoomUserSelector from '@/components/partials/war-room/WarRoomUserSelector.vue';
import { makeUrl } from '@/components/utils/browser/url';
import { sharableTypes } from '@/components/partials/war-room/WarRoomShare/constants';
import { categoryNames } from '@/components/partials/war-room/WarRoomPostForm/WarRoomPostFormCategories/constants';
import { isPostContentADealStory } from '@/types/typeHelpers';
import type { IPost } from '@/types/community';
import { SIGN_UP_MODAL } from '~/constants';
import type { RootState } from '~/types/store';

type WarRoomPostProps = {
  post: IPost;
};

const props = defineProps<WarRoomPostProps>();

const { $auth, $modal, $analytics, $sentryUtils } = useContext();
const router = useRouter();
const store = useStore<RootState>();

const sharableType = sharableTypes.POST;

const topics = computed(() => props.post.topic_ids.map(id => store.state.warRoom.topic_dict[id]));
const isAuthor = computed(() => (props.post.author ? props.post.user_username === $auth.user?.username : false));
const isDealStory = computed(() => props.post.category.slug === categoryNames.WIN_WIRE);
const dealCompany = computed(() =>
  isPostContentADealStory(props.post.content.data) && props.post.content.data.deal.company
    ? props.post.content.data.deal.company.name
    : undefined,
);
const dealDepartments = computed(() =>
  isPostContentADealStory(props.post.content.data)
    ? props.post.content.data.deal.departments?.map(d => d.name) || []
    : [],
);
const sharableUrl = computed(() =>
  makeUrl(
    router.resolve({
      name: 'war-room-posts-postSlug',
      params: { postSlug: props.post.slug },
    }).href,
  ),
);

async function onPollVote(choiceId: number) {
  if (!$auth?.loggedIn) {
    $modal.show(SIGN_UP_MODAL);
    return;
  }

  try {
    $analytics?.logEvent('war-room__post__poll-choice__click', { choiceId });

    await store.dispatch('warRoom/addPollChoice', { postSlug: props.post.slug, choiceId });
  } catch (e) {
    if (e instanceof Error) {
      try {
        Vue.prototype.$notice.error(e);
      } catch (e) {
        // Supress error
      }
    }
    $sentryUtils.error(e);
  }
}
</script>

<style scoped lang="scss">
.war-room-post {
  &__title {
    color: var(--theme-color-text);
    margin: 20px 0;
    word-break: break-word;
  }

  &__files,
  &__poll {
    margin-top: 20px;
  }

  &__topics {
    min-height: 37px;
  }

  &__actions {
    margin-top: 8px;
    margin-bottom: 32px;
    display: flex;
  }

  &__vote {
    margin-right: 8px;
  }

  &__deal-badge {
    margin-top: 20px;
  }
}
</style>
